import React, { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormDropdown, FormFields, FormTextField, Stack } from "sprint-common-web";

import { commandTextFieldTooltipContent } from "../common/commandTextFieldTooltipContent";
import { useFfmpegCommandValidation } from "../common/useFfmpegCommandValidation";
import { useCreateEncodeSpecificationForm } from "./useCreateEncodeSpecificationForm";

export interface CreateEncodeSpecificationMpeg4FormProps {
    onCancel(): void;
    onSubmit(): void;
}

export interface CreateEncodeSpecificationMpeg4FormInputs {
    name: string;
    ffmpegCommand: string;
    framerateProfileId: string;
    targetAspectRatioId: string;
}

export const CreateEncodeSpecificationMpeg4Form: FunctionComponent<CreateEncodeSpecificationMpeg4FormProps> = (
    props: CreateEncodeSpecificationMpeg4FormProps
) => {
    const { errors, control, formState, reset, handleSubmit } = useForm<CreateEncodeSpecificationMpeg4FormInputs>({
        defaultValues: {
            name: "",
            ffmpegCommand: "",
            framerateProfileId: "",
            targetAspectRatioId: ""
        },
        criteriaMode: "all"
    });

    const {
        framerateProfileOptions,
        validateNameUnique,
        createEncodeSpecMpeg4,
        aspectRatioOptions
    } = useCreateEncodeSpecificationForm();
    const { validateFfmpegCommand } = useFfmpegCommandValidation();

    const handleCreateEncodeSpecification = async (inputs: CreateEncodeSpecificationMpeg4FormInputs) => {
        await createEncodeSpecMpeg4(inputs);
        props.onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        props.onCancel();
    };

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleCreateEncodeSpecification)}>
                <Stack verticalAlign="space-between">
                    <FormFields>
                        <FormTextField
                            label="Name"
                            control={control}
                            name="name"
                            errors={errors}
                            rules={{
                                required: "Name is required field",
                                validate: validateNameUnique
                            }}
                        />
                        <FormTextField
                            label="Ffmpeg command"
                            control={control}
                            name="ffmpegCommand"
                            errors={errors}
                            multiline={true}
                            height={200}
                            rules={{
                                required: "Ffmpeg command is required field",
                                validate: {
                                    validateFfmpegCommand
                                }
                            }}
                            tooltipContent={commandTextFieldTooltipContent}
                        />
                        <FormDropdown
                            options={framerateProfileOptions}
                            label="Framerate profile"
                            name="framerateProfileId"
                            control={control}
                            errors={errors}
                            rules={{ required: "Select framerate profile" }}
                        />
                        <FormDropdown
                            options={aspectRatioOptions}
                            label="Target aspect ratio"
                            name="targetAspectRatioId"
                            control={control}
                            errors={errors}
                            rules={{ required: "Select target aspect ratio" }}
                        />
                    </FormFields>
                    <FormActions
                        isLoading={formState.isSubmitting || formState.isValidating}
                        onCancel={handleCancelButtonClick}
                    />
                </Stack>
            </Form>
        </Stack>
    );
};
